/**
 * Eric 2026-06-25 (iter120 — Session A2) — Barre d'actions de la
 * transcription Whisper.
 *
 * Extrait de VideoDetailPage.js (5677 lignes → cible <1500).
 *
 * Affiché uniquement sur le sous-onglet "whisper" pour les éditeurs
 * (canEditContent côté parent). Contient 3 actions :
 *   1. Historique des modifications (ouvre TranscriptHistoryModal).
 *   2. Détecter / valider les URLs prononcées (POST /validate-urls).
 *   3. Regrouper les segments Whisper en paragraphes ~30 s
 *      (POST /regroup-segments — Vincent FLHQ VV 2026-04-30).
 *
 * Vincent #355 (Eric 2026-06-21 SPEC STRICK 2e passe) — La barre entière
 * est gated par `show` côté parent ; un non-éditeur voit la transcription
 * en lecture seule sans cette barre.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import { ClockCounterClockwise, ArrowClockwise, Broom, Gear } from "@phosphor-icons/react";
import TranscriptionSettingsModal from "./TranscriptionSettingsModal";
import WhisperDiagnosticModal from "./WhisperDiagnosticModal";
import { GROUP_SETTINGS, GROUP_RERUN, GROUP_UTIL, GROUP_UTIL_ALERT, ToolbarSeparator, ToolbarGroupLabel } from "../../lib/transcriptToolbarStyles";
import { useCollapsedToolbarGroups } from "../../lib/useCollapsedToolbarGroups";

export const WhisperToolbar = ({
  show,
  lang,
  api,
  videoId,
  setShowHistory,
  loadRevisions,
  loadVideoData,
  loadAllTranscripts,
  setUrlProposals,
  urlValidating,
  setUrlValidating,
  startPolling,
  // Vincent B612 (Eric 2026-07-29 SPEC STRICK) — Toggles Diarisation /
  // ID faciale à passer au backend lors du "Refaire transcription Whisper"
  // pour que le pipeline utilise l'état ACTUEL des cases (et non l'ancien
  // état persisté sur la vidéo). Ceci met à jour la couleur du pipeline
  // immédiatement (rouge si off, jaune si en cours, vert si actif).
  useDiarisation = true,
  useFacialId = true,
  // Vincent B778 v2 (Eric 2026-02-18 SPEC STRICK PROD) — Durée totale de la
  // vidéo (secondes) passée au modal Réglages Whisper pour que l'auto-retry
  // A/B à `start + 60s` reste borné par la fin du fichier.
  videoDuration = 0,
  // Vincent B787 (Eric 2026-02-18 SPEC STRICK PROD) — Heuristique
  // « transcription suspecte » → fait clignoter le bouton Diagnostic.
  diagnosticSuspicious = false,
  // Vincent B945v2 (Eric 2026-02-14 SPEC STRICK PROD) — Slot pour injecter
  // les boutons fusion (Réglages fusion, Refaire la fusion) juste avant
  // « URLs » dans la MÊME toolbar. Consolidé du bloc Line 2 de
  // TranscriptPanel pour respecter la demande utilisateur : « tous les
  // boutons dans un seul bloc ». Render prop = React node.
  extraFusionButtons = null,
  // Vincent B971 (Eric 2026-02-26 SPEC STRICK PROD) — Passe allTranscripts
  // pour appliquer le pattern « toujours visible + disabled » quand
  // has_whisper est absent (parité avec FirefliesSubTab / MergedToolbar).
  allTranscripts = null,
}) => {
  // B728 (Eric 2026-02-13) — Modal des réglages Whisper / AssemblyAI.
  const [settingsModal, setSettingsModal] = useState(null); // "whisper" | "assemblyai" | null
  // Vincent B785 (Eric 2026-02-18 SPEC STRICK PROD) — Modal Diagnostic Whisper.
  const [diagnosticOpen, setDiagnosticOpen] = useState(false);
  // Tâche 1312 — Blocs repliables, choix mémorisé.
  const { isCollapsed, toggleGroup } = useCollapsedToolbarGroups();
  if (!show) return null;

  // Vincent B971 (Eric 2026-02-26 SPEC STRICK PROD) — Pattern
  // « toujours visible + disabled ». Les boutons qui nécessitent une
  // transcription Whisper existante restent rendus mais sont désactivés
  // avec un tooltip explicatif si `has_whisper` est absent.
  const hasWhisper = !!allTranscripts?.summary?.has_whisper;
  const disabledIfNoWhisperTitle = lang === "fr"
    ? "Aucune transcription Whisper disponible. Lance « Refaire transcription Whisper » pour activer ce bouton."
    : "No Whisper transcript available. Run 'Re-run Whisper' first to enable this button.";

  const handleValidateUrls = async () => {
    if (urlValidating) return;
    setUrlValidating(true);
    setUrlProposals(null);
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/validate-urls`,
        {},
        { withCredentials: true }
      );
      setUrlProposals(data);
      if ((data.proposals || []).length > 0) {
        toast.info(
          lang === "fr"
            ? `${data.detected} URL(s) détectée(s) — voir l'onglet Fusionnée`
            : `${data.detected} URL(s) detected — see Merged tab`
        );
      }
    } catch (err) {
      toast.error(
        lang === "fr"
          ? `Erreur : ${err.response?.data?.detail || err.message}`
          : `Error: ${err.response?.data?.detail || err.message}`
      );
    }
    setUrlValidating(false);
  };

  const handleRegroup = async () => {
    const ok = await confirmDialog(
      lang === "fr"
        ? "Regrouper les paragraphes Whisper en blocs d'environ 40 s (max 1 min 30) par locuteur ?"
        : "Regroup Whisper paragraphs into ~30s per-speaker blocks (cap 1min30)?",
      { confirmLabel: lang === "fr" ? "Regrouper" : "Regroup", tone: "warning" },
    );
    if (!ok) return;
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/regroup-segments`,
        {},
        { withCredentials: true }
      );
      toast.success(
        lang === "fr"
          ? `Regroupement OK : ${data.raw_segments} → ${data.grouped_segments} paragraphes`
          : `Regroup OK: ${data.raw_segments} → ${data.grouped_segments} paragraphs`
      );
      await loadVideoData();
      await loadAllTranscripts();
    } catch (err) {
      toast.error(
        lang === "fr"
          ? `Erreur : ${err.response?.data?.detail || err.message}`
          : `Error: ${err.response?.data?.detail || err.message}`
      );
    }
  };

  return (
    <div
      className="px-3 py-2.5 bg-amber-50 border-b border-amber-200 text-[11px]"
      data-testid="transcript-actions-bar"
    >
      {/* Vincent B926 (Eric 2026-02-14 SPEC STRICK PROD) — Texte
          « Vidéo importée avant la règle ~40 s par paragraphe… »
          supprimé (Vincent : ne plus l'afficher, il pollue les 3
          sous-onglets). Le bouton « Regrouper les paragraphes »
          reste, il fait toujours le travail à la demande.
          Ordre imposé par Vincent (B926), revu par la tâche 1308 :
            0. « Whisper : »                            (ToolbarGroupLabel)
            1. Réglages                                 (GROUP_SETTINGS)
            2. Diagnostic                               (GROUP_UTIL / ALERT si suspect)
            3. Refaire                                  (GROUP_RERUN)
            4. Nettoyer répétitions                     (GROUP_UTIL)
            5. Regrouper les paragraphes                (GROUP_UTIL)
            6. « Assembly AI : »                        (ToolbarGroupLabel)
            7. Assembly AI                              (GROUP_SETTINGS)
            8. Réappliquer diarisation micro-tours      (GROUP_RERUN)
            9. « Fusion : » + Réglages + Refaire        (extraFusionButtons)
           10. URLs                                     (GROUP_UTIL)
           11. Historique                                (GROUP_UTIL)
          3 groupes visuellement distincts (styles partagés dans
          `lib/transcriptToolbarStyles.js`). */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Tâche 1308 (Eric 2026-06-30) — Le mot « Whisper : » porte ce que
            les boutons ne répètent plus (« Réglages », « Diagnostic »,
            « Refaire »). */}
        <ToolbarGroupLabel
          testid="toolbar-group-label-whisper"
          collapsed={isCollapsed("whisper")}
          onToggle={() => toggleGroup("whisper")}
          count={5}
        >
          {lang === "fr" ? "Whisper :" : "Whisper:"}
        </ToolbarGroupLabel>
        {!isCollapsed("whisper") && (
          <>
        {/* 1. Réglages */}
        <button
          onClick={() => setSettingsModal("whisper")}
          className={GROUP_SETTINGS}
          data-testid="whisper-settings-btn"
          data-button-group="settings"
          title={lang === "fr" ? "Régler la langue, le prompt et la température de Whisper pour cette vidéo" : "Adjust Whisper language, prompt and temperature for this video"}
        >
          <Gear size={12} weight="bold" />
          {lang === "fr" ? "Réglages" : "Settings"}
        </button>
        {/* 2. Transcrire / Re-transcrire — Tâche 1348 (Eric 2026-06-30) :
            placé entre « Réglages » et « Diagnostic », dans l'ordre réel du
            travail (on règle, on transcrit, puis on audite). */}
        <button
          onClick={async () => {
            const ok = await confirmDialog(
              lang === "fr"
                ? "Refaire la transcription Whisper à partir de l'audio ?\n\nCela va :\n  • Effacer la transcription Whisper actuelle\n  • Effacer la transcription fusionnée (plus valide)\n  • Relancer l'analyse audio (30 s à plusieurs minutes)\n\nLa transcription Fireflies importée est conservée. Vous devrez cliquer sur « Refusionner » après.\n\nContinuer ?"
                : "Re-run Whisper transcription from audio?\n\nWill erase current Whisper + merged transcripts. Fireflies preserved. Re-run merge afterwards.",
              { tone: "warning" }
            );
            if (!ok) return;
            const toastId = toast.loading(
              lang === "fr" ? "🎧 Relance de la transcription Whisper…" : "🎧 Re-running Whisper…",
              { duration: Infinity }
            );
            try {
              const { data } = await axios.post(
                `${api}/api/videos/${videoId}/rerun-whisper`,
                { use_diarisation: useDiarisation, use_facial_id: useFacialId },
                { withCredentials: true, timeout: 60000 }
              );
              toast.success(
                lang === "fr"
                  ? `🎧 Transcription relancée en arrière-plan. La page se rafraîchira automatiquement. ${data.whisper_deleted} transcription(s) précédente(s) effacée(s).`
                  : `🎧 Whisper re-started in background. ${data.whisper_deleted} previous transcript(s) erased.`,
                { id: toastId, duration: 10000 }
              );
              startPolling?.();
              loadVideoData?.();
              setTimeout(() => loadAllTranscripts?.(), 15000);
              setTimeout(() => loadVideoData?.(), 15000);
            } catch (e) {
              toast.error(
                lang === "fr"
                  ? `❌ Échec : ${e.response?.data?.detail || e.message}`
                  : `❌ Failed: ${e.response?.data?.detail || e.message}`,
                { id: toastId, duration: 10000 }
              );
            }
          }}
          className={GROUP_RERUN}
          data-testid="rerun-whisper-btn"
          data-button-group="rerun"
          title={lang === "fr" ? "Relance la transcription Whisper depuis l'audio original (efface l'ancienne + la fusion)" : "Re-run Whisper from original audio"}
        >
          <ArrowClockwise size={12} weight="bold" />
          {/* Tâche 1348 — « Refaire » mentait quand rien n'avait encore été
              transcrit. Le verbe suit l'état réel de la vidéo. */}
          {lang === "fr"
            ? (hasWhisper ? "Re-transcrire" : "Transcrire")
            : (hasWhisper ? "Re-transcribe" : "Transcribe")}
        </button>
        {/* 3. Diagnostic (variante alerte si suspect) */}
        <button
          onClick={() => setDiagnosticOpen(true)}
          className={`${diagnosticSuspicious ? `${GROUP_UTIL_ALERT} animate-pulse` : GROUP_UTIL} disabled:opacity-50 disabled:cursor-not-allowed`}
          disabled={!hasWhisper}
          data-testid="whisper-diagnostic-btn"
          data-button-group="util"
          data-suspicious={diagnosticSuspicious ? "true" : "false"}
          aria-live={diagnosticSuspicious ? "polite" : undefined}
          title={!hasWhisper
            ? disabledIfNoWhisperTitle
            : (diagnosticSuspicious
              ? (lang === "fr" ? "⚠️ Transcription suspecte : < 20 segments pour une vidéo > 5 min. Clique pour diagnostiquer." : "⚠️ Suspect transcript. Click to diagnose.")
              : (lang === "fr" ? "Auditer la transcription Whisper : durée réelle, segments avant/après filtre, verdict automatique" : "Audit the Whisper transcript"))}
        >
          <span aria-hidden="true">{diagnosticSuspicious ? "⚠️" : "🩺"}</span>
          {lang === "fr" ? "Diagnostic" : "Diagnostic"}
        </button>
        {/* 4. Nettoyer répétitions Whisper */}
        <button
          onClick={async () => {
            const ok = await confirmDialog(
              lang === "fr"
                ? "Nettoyer les répétitions Whisper de cette vidéo ?\n\nCela retire les segments dupliqués et les hallucinations du prompt Whisper (bug OpenAI Whisper API v1). La transcription fusionnée sera invalidée — vous devrez cliquer sur « Refusionner » ensuite.\n\nIdempotent (safe à re-cliquer)."
                : "Clean Whisper repetitions on this video?\n\nRemoves duplicated segments and prompt hallucinations (OpenAI Whisper API v1 bug). Merged transcript will be invalidated — re-run merge afterwards.\n\nIdempotent (safe to re-click).",
              { tone: "warning" }
            );
            if (!ok) return;
            const toastId = toast.loading(
              lang === "fr" ? "🧹 Nettoyage des répétitions Whisper…" : "🧹 Cleaning Whisper repetitions…",
              { duration: Infinity }
            );
            try {
              const { data } = await axios.post(
                `${api}/api/videos/${videoId}/dedupe-whisper`,
                {},
                { withCredentials: true, timeout: 60000 }
              );
              if (data.status === "noop") {
                toast.info(
                  lang === "fr" ? "✅ Transcription déjà propre — aucun segment à retirer." : "✅ Transcript already clean.",
                  { id: toastId, duration: 6000 }
                );
              } else {
                toast.success(
                  lang === "fr"
                    ? `🧹 Nettoyage réussi : ${data.removed} segment(s) retiré(s) (${data.prompt_echo} écho(s) du prompt, ${data.repeats} répétition(s)). Relancez la fusion pour obtenir une transcription propre.`
                    : `🧹 Cleaned: ${data.removed} segment(s) removed. Re-run merge.`,
                  { id: toastId, duration: 12000 }
                );
              }
              await loadAllTranscripts?.();
              await loadVideoData?.();
            } catch (e) {
              toast.error(
                lang === "fr" ? `❌ Échec : ${e.response?.data?.detail || e.message}` : `❌ Failed: ${e.response?.data?.detail || e.message}`,
                { id: toastId, duration: 10000 }
              );
            }
          }}
          className={`${GROUP_UTIL} disabled:opacity-50 disabled:cursor-not-allowed`}
          disabled={!hasWhisper}
          data-testid="dedupe-whisper-btn"
          data-button-group="util"
          title={!hasWhisper
            ? disabledIfNoWhisperTitle
            : (lang === "fr" ? "Retire les répétitions et hallucinations Whisper (bug connu de l'API OpenAI Whisper v1)" : "Remove Whisper repetitions and hallucinations")}
        >
          <Broom size={12} weight="bold" />
          {lang === "fr" ? "Nettoyer répétitions" : "Clean repeats"}
        </button>
        {/* 5. Regrouper les paragraphes */}
        <button
          onClick={handleRegroup}
          className={`${GROUP_UTIL} disabled:opacity-50 disabled:cursor-not-allowed`}
          disabled={!hasWhisper}
          data-testid="regroup-segments-btn"
          data-button-group="util"
          title={!hasWhisper
            ? disabledIfNoWhisperTitle
            : (lang === "fr" ? "Regrouper les segments Whisper en paragraphes ~40 s (max 1 min 30) par locuteur" : "Regroup Whisper segments into ~30s per-speaker paragraphs (cap 1min30)")}
        >
          <ArrowClockwise size={12} weight="bold" />
          {lang === "fr" ? "Regrouper paragraphes" : "Regroup paragraphs"}
        </button>
          </>
        )}
        {/* Vincent B971 (Eric 2026-02-26 SPEC STRICK PROD) — Séparateur
            entre le bloc « Whisper » et le bloc « Assembly AI ». */}
        <ToolbarSeparator />
        {/* Tâche 1308 — Le mot « Assembly AI : » porte le fournisseur ; le
            bouton de réglages ne répète plus « Réglages ». */}
        <ToolbarGroupLabel
          testid="toolbar-group-label-assemblyai"
          collapsed={isCollapsed("assemblyai")}
          onToggle={() => toggleGroup("assemblyai")}
          count={2}
        >
          {lang === "fr" ? "Assembly AI :" : "Assembly AI:"}
        </ToolbarGroupLabel>
        {!isCollapsed("assemblyai") && (
          <>
        {/* 6. Assembly AI (réglages) */}
        <button
          onClick={() => setSettingsModal("assemblyai")}
          className={GROUP_SETTINGS}
          data-testid="assemblyai-settings-btn"
          data-button-group="settings"
          title={lang === "fr" ? "Régler la langue, le modèle et les locuteurs attendus d'AssemblyAI pour cette vidéo" : "Adjust AssemblyAI language, model and expected speakers for this video"}
        >
          <Gear size={12} weight="bold" />
          {/* Tâche 1348 — Le bouton répétait le nom du groupe (« Assembly AI »
              sous le libellé « Assembly AI : ») sans dire ce qu'il fait. */}
          {lang === "fr" ? "Réglages" : "Settings"}
        </button>
        {/* 7. Réappliquer diarisation micro-tours */}
        <button
          onClick={async () => {
            const ok = await confirmDialog(
              lang === "fr"
                ? "Réappliquer la diarisation avec split des micro-tours (B733) ?\n\nCela va :\n  • Relancer UNIQUEMENT AssemblyAI (~15-45 s en arrière-plan)\n  • Splitter les segments Whisper là où le client relance brièvement (« Oui », « Cool », « Parfait »)\n  • Recomposer les paragraphes avec la vraie répartition des locuteurs\n\nWhisper, la fusion Fireflies et la synthèse sont CONSERVÉES.\n\nContinuer ?"
                : "Re-apply diarization with micro-turn split (B733)?\n\nWill re-run ONLY AssemblyAI (~15-45s in background), split Whisper segments on speaker changes, and rebuild paragraphs. Whisper/Fireflies/synthesis preserved.",
              { tone: "warning" }
            );
            if (!ok) return;
            const toastId = toast.loading(
              lang === "fr" ? "🎯 Réapplication des micro-tours…" : "🎯 Re-applying micro-turns…",
              { duration: Infinity }
            );
            try {
              await axios.post(
                `${api}/api/videos/${videoId}/reapply-micro-turns`,
                {},
                { withCredentials: true, timeout: 30000 }
              );
              toast.success(
                lang === "fr"
                  ? "🎯 Réapplication démarrée en arrière-plan. L'onglet Locuteurs/Transcription se rafraîchira automatiquement à la fin."
                  : "🎯 Started in background. Speakers/Transcript tab will refresh automatically when done.",
                { id: toastId, duration: 10000 }
              );
              startPolling?.();
              loadVideoData?.();
              setTimeout(() => loadAllTranscripts?.(), 20000);
              setTimeout(() => loadVideoData?.(), 20000);
            } catch (e) {
              const detail = e.response?.data?.detail || e.message;
              toast.error(lang === "fr" ? `❌ Échec : ${detail}` : `❌ Failed: ${detail}`, { id: toastId, duration: 10000 });
            }
          }}
          className={`${GROUP_RERUN} disabled:opacity-50 disabled:cursor-not-allowed`}
          disabled={!hasWhisper}
          data-testid="reapply-micro-turns-btn"
          data-button-group="rerun"
          title={!hasWhisper
            ? disabledIfNoWhisperTitle
            : (lang === "fr" ? "Réapplique la diarisation avec split des micro-tours (B733) sans refaire Whisper" : "Re-apply diarization with micro-turn split (B733) without re-running Whisper")}
        >
          <ArrowClockwise size={12} weight="bold" />
          {lang === "fr" ? "Réappliquer diarisation micro-tours" : "Re-apply micro-turn diarization"}
        </button>
          </>
        )}
        {/* Tâche 1308 — « Diagnostic » a été remonté dans le bloc
            « Whisper : », juste après « Réglages ». Il n'est plus rendu ici. */}
        {/* Vincent B971 (Eric 2026-02-26 SPEC STRICK PROD) — Séparateur
            entre le bloc « Assembly AI » et le bloc « Fusion »
            (extraFusionButtons = « Fusion : » + Réglages + Refaire). */}
        <ToolbarSeparator />
        {/* Vincent B945v2 (Eric 2026-02-14 SPEC STRICK PROD) — Réglages
            fusion + Refaire la fusion, injectés depuis TranscriptPanel via
            le prop extraFusionButtons. Placés juste avant « URLs » pour
            respecter l'ordre demandé : […, Réglages fusion, URLs, Historique]. */}
        {extraFusionButtons}
        {/* Vincent B971 (Eric 2026-02-26 SPEC STRICK PROD) — Séparateur
            entre le bloc « Fusion » et le bloc final « Utilitaires »
            (URLs, Historique). */}
        <ToolbarSeparator />
        {/* 8. URLs */}
        <button
          onClick={handleValidateUrls}
          disabled={urlValidating || !hasWhisper}
          className={`${GROUP_UTIL} disabled:opacity-50 disabled:cursor-not-allowed`}
          data-testid="validate-urls-whisper-btn"
          data-button-group="util"
          title={!hasWhisper
            ? disabledIfNoWhisperTitle
            : (lang === "fr" ? "Detecter les URLs prononcees et vérifier qu'elles existent" : "Detect spoken URLs and verify they exist")}
        >
          🔗 {urlValidating ? "..." : lang === "fr" ? "URLs" : "URLs"}
        </button>
        {/* 9. Historique */}
        <button
          onClick={() => { setShowHistory(true); loadRevisions(); }}
          disabled={!hasWhisper}
          className={`${GROUP_UTIL} disabled:opacity-50 disabled:cursor-not-allowed`}
          data-testid="transcript-history-btn"
          data-button-group="util"
          title={!hasWhisper
            ? disabledIfNoWhisperTitle
            : (lang === "fr" ? "Historique des modifications" : "Modification history")}
        >
          <ClockCounterClockwise size={12} weight="bold" />
          {lang === "fr" ? "Historique" : "History"}
        </button>
      </div>
      <TranscriptionSettingsModal
        open={!!settingsModal}
        engine={settingsModal || "whisper"}
        api={api}
        videoId={videoId}
        videoDuration={videoDuration}
        lang={lang}
        onClose={() => setSettingsModal(null)}
        onRerun={() => {
          // B728 enhancement (Eric 2026-02-13) — après « Enregistrer & relancer »,
          // redémarre le polling pipeline + rafraîchit les données comme le fait
          // déjà le bouton « Refaire transcription Whisper ».
          startPolling?.();
          loadVideoData?.();
          setTimeout(() => loadAllTranscripts?.(), 15000);
          setTimeout(() => loadVideoData?.(), 15000);
        }}
      />
      {/* Vincent B785 (Eric 2026-02-18 SPEC STRICK PROD) — Modal
          Diagnostic Whisper (audit sans DevTools). */}
      <WhisperDiagnosticModal
        open={diagnosticOpen}
        videoId={videoId}
        api={api}
        lang={lang}
        onClose={() => setDiagnosticOpen(false)}
      />
    </div>
  );
};

export default WhisperToolbar;
